import { UseFormReturn, Path, useForm, SubmitHandler } from "react-hook-form";
import { ApolloError, useApolloClient } from "@apollo/client";
import { omit } from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ChakraProps } from "@chakra-ui/react";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { getFragmentData, gql } from "__generated__/gql";
import { SkuPricingCalculationInput } from "__generated__/gql/graphql";
import { patchCachedQuery } from "utils/apollo/cacheHelpers";
import { trackEvent } from "utils/segment";
import { definedDecimalStr } from "utils/schemas/decimalStr";
import { formatCurrency, formatPercent } from "utils/formatting";

export const INPUT_FIELD_NAMES = [
  "avgFreightOut",
  "grossProfitMargin",
  "isDistributorMarkup",
  "distributorPercent",
  "retailerMargin",
  "anticipatedDeduction",
] as const;

export type InputFieldNames = typeof INPUT_FIELD_NAMES[number];

export type Inputs = Pick<SkuPricingCalculationInput, InputFieldNames> & {
  unitCogs?: Decimal | null;
};

export type UsePricingCalculatorFormReturn = UseFormReturn<Inputs>;

export type InputComponentProps = ChakraProps & {
  formContext: UsePricingCalculatorFormReturn;
  loading?: boolean;
  name: Path<Omit<Inputs, "isDistributorMarkup">>;
};

export const DEFAULT_VALUES: Partial<Inputs> = {
  unitCogs: "2.00",
  avgFreightOut: "0.2",
  grossProfitMargin: "0.4",
  isDistributorMarkup: false,
  distributorPercent: "0.1",
  retailerMargin: "0.4",
  anticipatedDeduction: "0.33",
};

const schema: yup.ObjectSchema<Inputs> = yup
  .object()
  .shape({
    unitCogs: definedDecimalStr(),
    avgFreightOut: definedDecimalStr({ numberFormatter: formatCurrency })
      .min(-1e6, { isExclusive: true })
      .max(1e6, { isExclusive: true }),
    grossProfitMargin: definedDecimalStr({ numberFormatter: formatPercent })
      .min(0)
      .max(1e1, { isExclusive: true }),
    isDistributorMarkup: yup.bool().defined().default(false),
    distributorPercent: definedDecimalStr({ numberFormatter: formatPercent })
      .min(0)
      .max(1e1, { isExclusive: true }),
    retailerMargin: definedDecimalStr({ numberFormatter: formatPercent })
      .min(0)
      .max(1e1, { isExclusive: true }),
    anticipatedDeduction: definedDecimalStr({ numberFormatter: formatPercent })
      .min(0)
      .max(1e1, { isExclusive: true }),
  })
  .required();

export function usePricingCalculatorForm(): UsePricingCalculatorFormReturn {
  return useForm<Inputs>({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });
}

export const skuPricingCalculationFieldsFragment = gql(/* GraphQL */ `
  fragment SkuPricingCalculationFields on SkuPricingCalculation {
    id
    avgFreightOut
    grossProfitMargin
    isDistributorMarkup
    distributorPercent
    retailerMargin
    anticipatedDeduction
  }
`);

export const skuPricingCalculatorQuery = gql(/* GraphQL */ `
  query SkuPricingCalculator($skuId: UUID!) {
    sku(id: $skuId) {
      ...SelectSkuMenuSku
      id
      name
      unitCogs
      productLine {
        id
        name
      }
      pricingCalculation: skuPricingCalculation {
        ...SkuPricingCalculationFields
      }
    }
  }
`);

const createSkuPricingCalculationMutation = gql(/* GraphQL */ `
  mutation CreateSkuPricingCalculation(
    $input: CreateSkuPricingCalculationInput!
  ) {
    createSkuPricingCalculation(input: $input) {
      skuPricingCalculation {
        ...SkuPricingCalculationFields
      }
    }
  }
`);

const updateSkuPricingCalculationMutation = gql(/* GraphQL */ `
  mutation UpdateSkuPricingCalculation(
    $input: UpdateSkuPricingCalculationInput!
  ) {
    updateSkuPricingCalculation(input: $input) {
      skuPricingCalculation {
        ...SkuPricingCalculationFields
      }
    }
  }
`);

export function useUpdatePricingCalculation(
  skuId: UUID | undefined,
): [
  SubmitHandler<Inputs>,
  { loading: boolean; error: ApolloError | undefined },
] {
  const client = useApolloClient();

  const [performCreate, { loading: loadingCreate, error: errorCreate }] =
    useBrandMutation(createSkuPricingCalculationMutation, {
      onCompleted: () => {
        trackEvent("Pricing Calculation Saved");
      },
    });

  const [performUpdate, { loading: loadingUpdate, error: errorUpdate }] =
    useBrandMutation(updateSkuPricingCalculationMutation, {
      onCompleted: () => {
        trackEvent("Pricing Calculation Saved");
      },
    });

  const handleSubmit: SubmitHandler<Inputs> = async (rawData) => {
    if (!skuId) return;

    const data = omit(rawData, ["unitCogs"]);

    const { data: skuIdData } = await client.query({
      query: skuPricingCalculatorQuery,
      variables: { skuId },
    });

    const { id } =
      getFragmentData(
        skuPricingCalculationFieldsFragment,
        skuIdData.sku?.pricingCalculation,
      ) || {};

    if (id) {
      await performUpdate({ variables: { input: { id, patch: data } } });
    } else {
      const { data: createData } = await performCreate({
        variables: { input: { skuPricingCalculation: { skuId, ...data } } },
      });
      const { skuPricingCalculation } =
        createData?.createSkuPricingCalculation || {};

      if (!skuPricingCalculation) return;

      patchCachedQuery({
        query: skuPricingCalculatorQuery,
        variables: { skuId },
        data: (existing) =>
          existing &&
          existing.sku && {
            ...existing,
            sku: {
              ...existing.sku,
              pricingCalculation: getFragmentData(
                skuPricingCalculationFieldsFragment,
                skuPricingCalculation,
              ),
            },
          },
      });
    }
  };

  return [
    handleSubmit,
    {
      loading: loadingCreate || loadingUpdate,
      error: errorCreate || errorUpdate,
    },
  ];
}

export const BIG_PINK_INPUT_PROPS = {
  variant: "unstyled",
  bg: "white",
  size: "3xl",
  color: "lightPink",
  fontWeight: 800,
  px: 3,
  pt: 1,
  pb: 2,
  lineHeight: 1.3,
};
